import cn from 'classnames'
import dynamic from 'next/dynamic'
import s from './Layout.module.scss'
import React, { FC, useState, useEffect } from 'react'
import { useRouter } from 'next/router'
import { useUI } from '@corratech/pylot-ui/context'
import { useAuthUI } from '@corratech/pylot-auth-manager'
import { FeatureBar, Navbar } from '@components/common'
import { useAcceptCookies } from '@corratech/pylot-utils'
import { Button } from '@corratech/pylot-ui'
import { LoadingIndicator } from '@components/LoadingIndicator'
import formStyles from '@commonStyles/Forms.module.scss'
import { LoginView } from '@lib/casaideas-components/pylot-auth'
import { useTranslation } from 'next-i18next/pages'
import { AuthWrapper } from '@components/common/AuthWrapper'
import { PromoBanner } from '@corratech/pylot-promobanner'
import { Cross, Trash } from '@components/icons'
import useIsMobile from '@lib/hooks/useIsMobile'
import useIsPWA from '@lib/hooks/useIsPWA'
import { usePreloadedData } from '@preload'
import { useCmsBlocks } from '@pylot-data/hooks/cms/use-cms-blocks'
import isIOSDevice from '@lib/utils/isIOSDevice'
import { useStoreConfig } from '@config/hooks/useStoreConfig'
import { ProductPrice } from '../ProductPrice/ProductPrice'
import WhatsappContainer from '../WhatsappContainer'
import { CartMixAlert } from '../CartMixAlert/CartMixAlert'
import { ImageConsentModal } from '../ImageConsentModal/ImageConsentModal'
import LoginPanel from '../LoginPanel/LoginPanel'
import {
    Cart,
    CartItemPrices,
    ConfigurableCartItem,
    GiftCardCartItem,
    Maybe,
    PriceRange,
    SimpleCartItem
} from '@pylot-data/pylotschema'
import { useCart } from '@corratech/pylot-cart-manager'
import {
    cartUpdateEvent,
    cartUpdateOnQuantityChange,
    cartUpdateOnEmptyCart
} from '@lib/utils/exponeaTrackingEvents'
import { formatCurrency } from '@lib/utils/formatCurrency'
import { useExponeaCarousel } from '@lib/hooks/useExponeaCarousel'
import debounce from 'lodash/debounce'
import Image from 'next/image'

const PWANavbar = dynamic(() => import('../Navbar/NavbarPwa'), { ssr: false })
const TabBar = dynamic(() => import('../TabBar'), { ssr: false })

type GeneralCartItem = ConfigurableCartItem | SimpleCartItem | GiftCardCartItem

const SignUpView = dynamic(
    () => import('@corratech/pylot-auth/src/SignUpView'),
    {
        // eslint-disable-next-line react/display-name
        loading: () => <LoadingIndicator />
    }
)
const ForgotPassword = dynamic(
    () => import('@corratech/pylot-auth/src/ForgotPassword'),
    {
        // eslint-disable-next-line react/display-name
        loading: () => <LoadingIndicator />
    }
)
const Footer = dynamic(() => import('@components/common/Footer'), {
    ssr: false
})

const Sidebar = dynamic(() => import('@corratech/pylot-ui/src/Sidebar'), {
    ssr: false
})

const CartSidebarView = dynamic(
    () => import('@corratech/pylot-cart/src/CartSidebarView')
)

const Modal = dynamic(() => import('@corratech/pylot-ui/src/Modal'), {
    ssr: false
})

const PromoBannerRichContent = dynamic(
    () =>
        import('@corratech/pylot-rich-content').then((mod) => mod.RichContent),
    { ssr: false }
)

interface Props {
    authRequired: boolean
    pageProps: any
}

export const RenderPrice = ({
    quantity,
    customTotal,
    productPriceRange
}: {
    itemPrice: Maybe<CartItemPrices> | undefined
    customTotal?: number
    productPriceRange: PriceRange
    quantity?: number
}): React.ReactElement => (
    <ProductPrice
        customTotal={customTotal}
        priceRange={productPriceRange}
        quantity={quantity}
    />
)

const PRODUCT_URL_PREFIX = '/producto'
const HOME_PREFIX = '/'

const Layout: FC<Props> = ({ children, authRequired }) => {
    const { displaySidebar, closeSidebar } = useUI()
    const { modalView, displayModal, closeAuthModal } = useAuthUI()
    const { acceptedCookies, onAcceptCookies } = useAcceptCookies()
    const { t } = useTranslation()
    const { data: cartData, mutate: updateCart } = useCart()
    const { pathname } = useRouter()
    const isPWA = useIsPWA()
    const isMobile = useIsMobile()
    const isCheckoutPath = pathname === '/checkout'
    const isHomePath = pathname === '/'
    const shouldDisplayTabBar = isHomePath && isMobile && isPWA
    const isIOS = isIOSDevice()

    const [cartCode, setCartCode] = useState('')

    useExponeaCarousel()

    const config = useStoreConfig()
    const { header: { variables } = {} } = config.preload

    const initialData = usePreloadedData('header')

    const { items, isValidating } = useCmsBlocks(variables?.identifiers ?? [], {
        initialData,
        revalidateOnMount: true,
        revalidateOnFocus: false
    })

    const onRemoveItemFromCart = (
        item: ConfigurableCartItem | SimpleCartItem | GiftCardCartItem
    ) => {
        cartUpdateEvent('remove', item, cartData?.data.cart)
    }

    const onRemoveAllItemsFromCart = (cart: Cart) => {
        cartUpdateOnEmptyCart(cart)
    }

    const onUpdateItemQuantity = (item: GeneralCartItem, value: number) => {
        const cart = cartData?.data.cart
        debounce(() => {
            cartUpdateOnQuantityChange(item, cart!, value)
        }, 2000)()
    }

    const onShowCartCodeModal = (code: string) => {
        setCartCode(code)
    }

    useEffect(() => {
        if (shouldDisplayTabBar) {
            const intervalId = setInterval(() => {
                const launcher = document.getElementById('launcher')
                if (launcher) {
                    if (isHomePath) {
                        launcher.style.bottom = isIOS ? '90px' : '70px'
                    } else {
                        launcher.style.bottom = '0px'
                    }
                    clearInterval(intervalId)
                }
            }, 1000)

            return () => clearInterval(intervalId)
        }
    }, [pathname, shouldDisplayTabBar, isHomePath])

    useEffect(() => {
        if (isPWA) {
            const intervalId = setInterval(() => {
                const launcher = document.getElementById('launcher')
                if (launcher) {
                    launcher.style.display = 'none'
                    clearInterval(intervalId)
                }
            }, 1000)
            return () => clearInterval(intervalId)
        }
    }, [pathname, isCheckoutPath, isPWA])

    const nav = isMobile && isPWA ? <PWANavbar /> : <Navbar />

    return (
        <div className={cn(s.root, { [s.isHome]: isHomePath })}>
            <a className={s['skip-to-content']} href="#contentarea">
                {t('Skip to Content')}
            </a>
            {/* {pathname === HOME_PREFIX ? <WhatsappContainer /> : null} */}
            {!isValidating && items && items.length > 0 && items[0] && (
                <PromoBanner
                    className={s['promobanner-custom']}
                    cookieName="promobanner"
                    closeButton={<Cross />}
                >
                    <PromoBannerRichContent html={items[0]?.content} />
                </PromoBanner>
            )}
            {nav}
            <CartMixAlert />
            <ImageConsentModal />
            <main className="fit" id="contentarea">
                <AuthWrapper authRequired={authRequired}>
                    {children}
                </AuthWrapper>
            </main>
            {isCheckoutPath ? null : <Footer />}
            {shouldDisplayTabBar ? <TabBar /> : null}
            {displayModal &&
                (modalView === 'LOGIN_VIEW' ||
                    modalView === 'SIGNUP_VIEW' ||
                    modalView === 'FORGOT_VIEW') && (
                    <LoginPanel
                        open={displayModal}
                        onClose={closeAuthModal}
                        labelledBy={
                            modalView === 'LOGIN_VIEW'
                                ? 'login-panel-title'
                                : undefined
                        }
                    >
                        <div className={cn(formStyles.root)}>
                            {modalView === 'LOGIN_VIEW' && (
                                <LoginView
                                    cartData={cartData}
                                    updateCart={updateCart}
                                />
                            )}
                            {modalView === 'SIGNUP_VIEW' && <SignUpView />}
                            {modalView === 'FORGOT_VIEW' && <ForgotPassword />}
                        </div>
                    </LoginPanel>
                )}
            {displaySidebar && (
                <Sidebar open={displaySidebar} onClose={closeSidebar}>
                    <CartSidebarView
                        RemoveItemIcon={
                            <div style={{ marginRight: '-5px' }}>
                                <Trash />
                            </div>
                        }
                        removeAllItemsEnabled
                        RenderPrice={RenderPrice}
                        onRemoveItemFromCart={onRemoveItemFromCart}
                        formatCurrency={formatCurrency}
                        productUrlPrefix={PRODUCT_URL_PREFIX}
                        // @ts-ignore
                        onUpdateItemQuantity={onUpdateItemQuantity}
                        onRemoveAllItemsFromCart={onRemoveAllItemsFromCart}
                        onShowCartCodeModal={onShowCartCodeModal}
                    />
                </Sidebar>
            )}
            {!!cartCode && (
                <Modal
                    open={!!cartCode}
                    onClose={() => setCartCode('')}
                    className={cn({
                        registerView: modalView === 'SIGNUP_VIEW'
                    })}
                >
                    <div className="flex flex-col justify-center justify-items-center">
                        <p>{t('cart|To use in store')}</p>
                        <Image
                            src={`https://api.qrserver.com/v1/create-qr-code/?size=240x240&data=${cartCode}`}
                            alt=""
                            width={240}
                            height={300}
                        />
                    </div>
                </Modal>
            )}
            {!acceptedCookies && (
                <FeatureBar
                    title={t(
                        'This site uses cookies to improve your experience.'
                    )}
                    extensionTitle={t(
                        'By clicking, you agree to our Privacy Policy.'
                    )}
                    extraExtensionTitle={t('Do you accept our privacy policy?')}
                    hide={acceptedCookies}
                    action={
                        <Button
                            className="mx-5"
                            onClick={() => onAcceptCookies()}
                        >
                            {t('Accept cookies')}
                        </Button>
                    }
                />
            )}
        </div>
    )
}

export default Layout
